/**
 * The live channel, as the browser sees it.
 *
 * One socket serves the whole page. The hero board and the activity feed are
 * two views of the same stream, so they share a connection rather than opening
 * one each — a second socket would double the fan-out cost of every bid to show
 * the same event twice.
 *
 * The channel is strictly a courier. It carries bids that have already cleared
 * and it invents nothing: when it is not connected, the surfaces above it fall
 * back to re-reading the board on a timer, and they animate the difference the
 * same way. That is the whole fallback — there is no fabricated activity to
 * cover a silent socket, because a board that moves without a payment behind it
 * would be a lie about the product's only claim.
 *
 * Lifecycle rules, in order of how often they bite:
 *   - a hidden tab holds no socket. Reopening on return costs one handshake and
 *     one refetch; holding one open for an unwatched page costs a connection
 *     for as long as the tab lives.
 *   - a reconnect closes the gap by refetching, because whatever happened while
 *     the socket was down was never delivered and never will be.
 *   - a failed connection backs off to thirty seconds and keeps trying quietly.
 *     Nothing in the interface reports it; the board simply polls instead.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { BidEvent } from '@bidtohire/shared';

import { useConfig } from './config';

/** How long a board waits between reads when there is no live channel. */
export const FALLBACK_POLL_MS = 60_000;

const FIRST_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

export interface LiveSubscriber {
  /** A bid that has cleared. Never called with anything else. */
  readonly onBid?: (event: BidEvent) => void;
  /**
   * The channel has just opened, or reopened after a gap. Anything that
   * happened while it was closed was missed, so a subscriber re-reads its data
   * here rather than assuming the stream is complete.
   */
  readonly onResume?: () => void;
}

interface LiveState {
  /** True only while a socket is open and carrying events. */
  readonly isLive: boolean;
  readonly subscribe: (subscriber: LiveSubscriber) => () => void;
}

const IDLE: LiveState = { isLive: false, subscribe: () => () => undefined };

const LiveContext = createContext<LiveState>(IDLE);

/** Parse a frame, returning undefined for anything that is not a bid. */
export function parseLiveMessage(data: unknown): BidEvent | undefined {
  if (typeof data !== 'string') return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return undefined;
  }
  if (parsed === null || typeof parsed !== 'object') return undefined;
  const candidate = parsed as Partial<BidEvent>;
  if (candidate.type !== 'bid') return undefined;
  if (typeof candidate.jobId !== 'string' || typeof candidate.totalCents !== 'number') {
    return undefined;
  }
  return candidate as BidEvent;
}

/** Exponential backoff with jitter, so a restarted API is not stampeded. */
export function retryDelayMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(MAX_RETRY_MS, FIRST_RETRY_MS * 2 ** Math.max(0, attempt));
  return Math.round(base * (0.75 + random() * 0.5));
}

/**
 * Holds the socket for as long as the application is mounted.
 *
 * The address comes from `GET /config`; a build with no channel deployed gets
 * no address, never opens a socket, and every subscriber simply stays on its
 * polling fallback.
 */
export function LiveProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const liveUrl = useConfig()?.liveUrl;
  const [isLive, setLive] = useState(false);

  const subscribers = useRef(new Set<LiveSubscriber>());

  const subscribe = useCallback((subscriber: LiveSubscriber): (() => void) => {
    subscribers.current.add(subscriber);
    return () => {
      subscribers.current.delete(subscriber);
    };
  }, []);

  useEffect(() => {
    if (liveUrl === undefined || liveUrl === '') return;
    if (typeof WebSocket !== 'function') return;

    let socket: WebSocket | undefined;
    let retry = 0;
    let timer: number | undefined;
    let disposed = false;

    const notifyResume = (): void => {
      for (const subscriber of subscribers.current) subscriber.onResume?.();
    };

    const close = (): void => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
      if (socket !== undefined) {
        // The handlers are dropped first so this deliberate close cannot be
        // mistaken for a dropped connection and trigger a reconnect.
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
          socket.close();
        }
        socket = undefined;
      }
      setLive(false);
    };

    const scheduleRetry = (): void => {
      if (disposed || document.hidden) return;
      timer = window.setTimeout(open, retryDelayMs(retry));
      retry += 1;
    };

    function open(): void {
      if (disposed || document.hidden || socket !== undefined) return;

      let opening: WebSocket;
      try {
        // The channel only ever listens, so the board it watches is named once,
        // in the query string, rather than in a message it would have to send.
        opening = new WebSocket(`${liveUrl}?scope=global`);
      } catch {
        scheduleRetry();
        return;
      }
      socket = opening;

      opening.onopen = () => {
        retry = 0;
        setLive(true);
        notifyResume();
      };

      opening.onmessage = (message: MessageEvent<unknown>) => {
        const event = parseLiveMessage(message.data);
        if (event === undefined) return;
        for (const subscriber of subscribers.current) subscriber.onBid?.(event);
      };

      const dropped = (): void => {
        if (socket !== opening) return;
        socket = undefined;
        setLive(false);
        scheduleRetry();
      };
      opening.onclose = dropped;
      opening.onerror = dropped;
    }

    const onVisibility = (): void => {
      if (document.hidden) {
        close();
        retry = 0;
      } else {
        open();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    open();

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      close();
    };
  }, [liveUrl]);

  const value = useMemo<LiveState>(() => ({ isLive, subscribe }), [isLive, subscribe]);

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

/**
 * Subscribe to cleared bids.
 *
 * The callbacks are held in a ref so a subscriber may close over fresh state
 * without re-subscribing on every render — resubscribing per render would drop
 * events in the gap between the two effects.
 */
export function useLiveBids(subscriber: LiveSubscriber): { readonly isLive: boolean } {
  const { isLive, subscribe } = useContext(LiveContext);
  const latest = useRef(subscriber);
  latest.current = subscriber;

  useEffect(
    () =>
      subscribe({
        onBid: (event) => latest.current.onBid?.(event),
        onResume: () => latest.current.onResume?.(),
      }),
    [subscribe],
  );

  return { isLive };
}

/**
 * The fallback: re-read on a timer whenever the live channel is not carrying.
 *
 * The interval is deliberately slow. It is not there to feel live — nothing
 * polls its way to live — it is there so a watcher without a socket still sees
 * a board that is true within the minute, and still sees it change in the same
 * motion when it does.
 */
export function useFallbackPolling(
  isLive: boolean,
  refetch: () => void,
  intervalMs = FALLBACK_POLL_MS,
): void {
  const latest = useRef(refetch);
  latest.current = refetch;

  useEffect(() => {
    if (isLive) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) latest.current();
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [isLive, intervalMs]);
}
